import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { CasovyUdajE } from '@generated/be';
import { pluralizeUnit } from '@shared/utils';

const CASOVY_UDAJ_TO_UNIT: Record<CasovyUdajE, 'days' | 'months' | 'years'> = {
  [CasovyUdajE.Dny]: 'days',
  [CasovyUdajE.Mesice]: 'months',
  [CasovyUdajE.Roky]: 'years',
};

/**
 * Maximální hodnota "Doby do archivace" podle zvolené jednotky (viz DIAREZA-5815 / wiki).
 * Všechny tři meze odpovídají téže reálné době – 2 roky.
 */
export const DOBA_DO_ARCHIVACE_MAX: Record<CasovyUdajE, number> = {
  [CasovyUdajE.Dny]: 730,
  [CasovyUdajE.Mesice]: 24,
  [CasovyUdajE.Roky]: 2,
};

/** Maximální hodnota "Doby archivace" – vždy v letech (1–100). */
export const DOBA_ARCHIVACE_MAX_ROKY = 100;

/**
 * Validátor maxima pro "Dobu do archivace" – limit se odvíjí od sourozeneckého controlu
 * `dobaDoArchivaceJednotka` (dny/měsíce/roky). Chybu vrací pod klíčem `max` ve stejném tvaru
 * jako Angular Validators.max, takže se použije standardní hláška "Maximální hodnota …".
 * Pozn.: při změně jednotky je potřeba na tomto controlu zavolat updateValueAndValidity().
 */
export function dobaDoArchivaceMaxValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value == null || value === '') {
      return null;
    }
    const jednotka = control.parent?.get('dobaDoArchivaceJednotka')?.value as CasovyUdajE | null;
    if (!jednotka) {
      return null;
    }
    const max = DOBA_DO_ARCHIVACE_MAX[jednotka];
    return Number(value) > max ? { max: { max, actual: Number(value) } } : null;
  };
}

/**
 * Zformátuje časový údaj (číslo + jednotka) do lidsky čitelné podoby s českým skloňováním,
 * např. `1 + ROKY` → „1 rok", `2 + ROKY` → „2 roky", `5 + MESICE` → „5 měsíců".
 * Používá se pro zobrazení Doby archivace / Doby do archivace v detailech.
 */
export function formatCasovyUdaj(value: number | null | undefined, jednotka: CasovyUdajE | null | undefined): string {
  if (value == null || !jednotka) {
    return '';
  }
  return `${value} ${pluralizeUnit(value, CASOVY_UDAJ_TO_UNIT[jednotka])}`;
}
